import { AriaDescriber } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Directive,
    ElementRef,
    HostBinding,
    Inject,
    Input,
    NgZone,
    Optional,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { IccAppearance } from '../../enums/appearance';

export type IccBadgePosition =
    | 'above after'
    | 'above before'
    | 'below before'
    | 'below after'
    | 'before'
    | 'after'
    | 'above'
    | 'below';

export type IccBadgeSize = 'small' | 'large';

let nextId = 0;

@Directive({
    selector: '[iccBadge]'
})
export class BadgeDirective {
    /** Whether the badge has any content. */
    _hasContent = false;
    _hasManyDigits = false;

    @HostBinding('class.icc-badge')
    class = true;

    @Input()
    @HostBinding('class.icc-badge-disabled')
    disabled = false;

    @Input('iccBadgeAppearance')
    get appearance(): IccAppearance {
        return this._appearance;
    }
    set appearance(value: IccAppearance) {
        this._setAppearance(value);
        this._appearance = value;
    }
    private _appearance: IccAppearance = null;

    /** Whether the badge should overlap its contents or not */
    @Input('iccBadgeOverlap')
    @HostBinding('class.icc-badge-overlap')
    get overlap(): boolean {
        return this._overlap;
    }
    set overlap(val: boolean) {
        this._overlap = coerceBooleanProperty(val);
    }
    private _overlap = true;

    /**
     * Position the badge should reside.
     * Accepts any combination of 'above'|'below' and 'before'|'after'
     */
    @Input('iccBadgePosition') position: IccBadgePosition = 'above after';

    /** The content for the badge */
    @Input('iccBadge') content: string | number | undefined | null;

    /** Message used to describe the decorated element via aria-describedby */
    @Input('iccBadgeDescription')
    get description(): string {
        return this._description;
    }
    set description(newDescription: string) {
        if (newDescription !== this._description) {
            const badgeElement = this._badgeElement;
            this._updateHostAriaDescription(newDescription, this._description);
            this._description = newDescription;

            if (badgeElement) {
                newDescription
                    ? badgeElement.setAttribute('aria-label', newDescription)
                    : badgeElement.removeAttribute('aria-label');
            }
        }
    }
    private _description = '';

    /** Size of the badge. Can be 'small', 'medium', or 'large'. */
    @Input('iccBadgeSize') size: IccBadgeSize = 'large';

    @HostBinding('class.icc-badge-small')
    get small(): boolean {
        return this.size === 'small';
    }

    @HostBinding('class.icc-badge-large-single-digit')
    get medium(): boolean {
        return this.size === 'large' && !this._hasManyDigits;
    }

    @HostBinding('class.icc-badge-large-multiple-digits')
    get large(): boolean {
        return this.size === 'large' && this._hasManyDigits;
    }

    /** Whether the badge is hidden. */
    @Input('iccBadgeHidden')
    get hidden(): boolean {
        return this._hidden;
    }
    set hidden(val: boolean) {
        this._hidden = coerceBooleanProperty(val);
    }
    private _hidden = false;

    @HostBinding('class.icc-badge-hidden')
    get isHidden() {
        return this.hidden;
    }

    /** Unique id for the badge */
    _id: number = nextId++;

    private _badgeElement: HTMLElement | undefined;

    constructor(
        private _ngZone: NgZone,
        private _elementRef: ElementRef<HTMLElement>,
        private _ariaDescriber: AriaDescriber,
        private _renderer: Renderer2,
        @Optional() @Inject(ANIMATION_MODULE_TYPE) private _animationMode?: string
    ) {}

    /** Whether the badge is above the host or not */
    @HostBinding('class.icc-badge-above')
    get isAbove(): boolean {
        return this.position.indexOf('below') === -1;
    }

    @HostBinding('class.icc-badge-below')
    get isBelow(): boolean {
        return this.position.indexOf('below') !== -1;
    }


    /** Whether the badge is after the host or not */
    @HostBinding('class.icc-badge-after')
    get isAfter(): boolean {
        return this.position.indexOf('before') === -1;
    }
    @HostBinding('class.icc-badge-before')
    get isBefore(): boolean {
        return this.position.indexOf('before') !== -1;
    }

    ngOnChanges(changes: SimpleChanges) {
        const contentChange = changes.content;

        if (contentChange) {
            const value = contentChange.currentValue;
            this._hasContent = value != null && `${value}`.trim().length > 0;
            this._hasManyDigits = value != null && `${value}`.trim().length > 1;
            this._updateTextContent();
        }
    }

    ngOnDestroy() {
        const badgeElement = this._badgeElement;

        if (badgeElement) {
            if (this.description) {
                this._ariaDescriber.removeDescription(badgeElement, this.description);
            }

            // When creating a badge through the Renderer, Angular will keep it in an index.
            // We have to destroy it ourselves, otherwise it'll be retained in memory.
            if (this._renderer.destroyNode) {
                this._renderer.destroyNode(badgeElement);
            }
        }
    }

    /**
     * Gets the element into which the badge's content is being rendered.
     * Undefined if the element hasn't been created (e.g. if the badge doesn't have content).
     */
    getBadgeElement(): HTMLElement | undefined {
        return this._badgeElement;
    }

    /** Injects a span element into the DOM with the content. */
    private _updateTextContent(): HTMLSpanElement {
        if (!this._badgeElement) {
            this._badgeElement = this._createBadgeElement();
        } else {
            this._badgeElement.textContent = this._stringifyContent();
        }
        return this._badgeElement;
    }

    /** Creates the badge element */
    private _createBadgeElement(): HTMLElement {
        const badgeElement = this._renderer.createElement('span');
        const activeClass = 'icc-badge-active';
        const contentClass = 'icc-badge-content';

        // Clear any existing badges which may have persisted from a server-side render.
        this._clearExistingBadges(contentClass);
        badgeElement.setAttribute('id', `icc-badge-content-${this._id}`);
        badgeElement.classList.add(contentClass);
        badgeElement.textContent = this._stringifyContent();

        if (this._animationMode === 'NoopAnimations') {
            badgeElement.classList.add('_mat-animation-noopable');
        }

        if (this.description) {
            badgeElement.setAttribute('aria-label', this.description);
        }

        this._elementRef.nativeElement.appendChild(badgeElement);

        // animate in after insertion
        if (
            typeof requestAnimationFrame === 'function' &&
            this._animationMode !== 'NoopAnimations'
        ) {
            this._ngZone.runOutsideAngular(() => {
                requestAnimationFrame(() => {
                    badgeElement.classList.add(activeClass);
                });
            });
        } else {
            badgeElement.classList.add(activeClass);
        }

        return badgeElement;
    }

    /** Sets the aria-label property on the element */
    private _updateHostAriaDescription(newDescription: string, oldDescription: string): void {
        // ensure content available before setting label
        const content = this._updateTextContent();

        if (oldDescription) {
            this._ariaDescriber.removeDescription(content, oldDescription);
        }

        if (newDescription) {
            this._ariaDescriber.describe(content, newDescription);
        }
    }

    private _setAppearance(appearance: IccAppearance) {
        if (appearance !== this._appearance) {
            const classList = this._elementRef.nativeElement.classList;
            if (this._appearance) {
                classList.remove(`icc-badge-${this._appearance}`);
            }
            if (appearance) {
                classList.add(`icc-badge-${appearance}`);
            }
        }
    }

    /** Clears any existing badges that might be left over from server-side rendering. */
    private _clearExistingBadges(cssClass: string) {
        const element = this._elementRef.nativeElement;
        let childCount = element.children.length;

        // Use a reverse while, because we'll be removing elements from the list as we're iterating.
        while (childCount--) {
            const currentChild = element.children[childCount];

            if (currentChild.classList.contains(cssClass)) {
                element.removeChild(currentChild);
            }
        }
    }

    /** Gets the string representation of the badge content. */
    private _stringifyContent(): string {
        // Convert null and undefined to an empty string which is consistent
        // with how Angular handles them in inside template interpolations.
        const content = this.content;
        return content == null ? '' : `${content}`;
    }
}
